import { slugify } from '@creator/ui/utils/url-utils';
import type { OutputData } from '@editorjs/editorjs';
import type { EditorJSMediaBlock } from '@src/basic-components/Media/Media';
import { getBaseUrl } from '@src/config';
import { commentsAtom } from '@src/store/comments/comments';
import { type Comment, type Post, PostStatus } from '@src/store/contentEntities/model';
import { getPosts } from '@src/store/posts/posts';
import { jotaiStore } from '@src/store/store';
import { ensureTokenUrl } from '../config/helpers';
import { getUserId } from '../user/helpers';

export function getPost(postId: string): Post | undefined {
    const allPosts = getPosts();
    return allPosts[postId];
}

export function getPostBySlugUrlOrId(postSlugOrId: string): Post | undefined {
    const allPosts = getPosts();
    if (allPosts[postSlugOrId]) return allPosts[postSlugOrId];

    return Object.values(allPosts).find(post => post.slugUrl === postSlugOrId);
}

export function getPostComment(commentId: string) {
    const commentsFromJotai = jotaiStore.get(commentsAtom)[commentId];
    return commentsFromJotai;
}

export function getPostTitleMaxLength(): number {
    return 150;
}

export function isPostDeleted(postId: string): boolean {
    const post = getPost(postId);
    if (!post) return false;

    return [1, 2, 3, 4].includes(post.status);
}

export function isPendingPost(postId: string): boolean {
    const post = getPost(postId);
    if (!post) return false;

    return post.status === 5;
}

export function isScheduledPost(postId: string): boolean {
    const post = getPost(postId);
    if (!post) return false;

    return post.status === PostStatus.PUBLISHED_SCHEDULED;
}

export function isDraftPost(postId: string): boolean {
    const post = getPost(postId);
    if (!post) return false;

    return post.status === PostStatus.DRAFT;
}

export function isPendingTranscriptPost(postId: string): boolean {
    const post = getPost(postId);
    if (!post) return false;

    return post.status === 6;
}

export function isResponsePost(postOrPostId: string | Post): boolean {
    const _post = typeof postOrPostId === 'object' ? postOrPostId : getPost(postOrPostId);
    if (!_post) return false;
    const { postTypes = [] } = _post;
    return postTypes.includes('response');
}

export function parsePostContent(post: Post): EditorJS.OutputData | null {
    let postContent: OutputData | null;
    try {
        postContent = JSON.parse(post.postContent);
    } catch (error) {
        return null;
    }

    return postContent;
}

export function getMediaPoster(url: string): string {
    const lastSegment = url.split('/').pop();
    if (lastSegment?.includes('.')) {
        const originalType = url.split('.').pop() || ''; // get file extensions
        return url.replace(originalType, 'jpeg?alt=media');
    }

    return url.replace('?alt=media', '.jpeg?alt=media');
}

export function hasMediaBlock(blocks: EditorJS.OutputBlockData[]): boolean {
    return Boolean(blocks.find(block => block.type === 'video' || block.type === 'audio'));
}

export function hasNewlyUploadedMediaBlock(blocks: EditorJS.OutputBlockData[]): boolean {
    return Boolean(blocks.find(block => block.type === 'video' && block.data.url.includes('newBlockId='))); // block.type === 'audio'
}

export function getMediaBlockData(type: EditorJSMediaBlock, url: string, thumbnailUrl: string): EditorJS.OutputBlockData['data'] {
    if (type === 'video') {
        return {
            url: url,
            caption: '',
            autoplay: false,
            controls: true,
            muted: false,
            stretched: true,
            width: 0,
            height: 0,
            image: {
                url: thumbnailUrl,
            },
        };
    }
    // Audio
    return {
        url: url,
        caption: '',
        controls: true,
        image: {
            url: thumbnailUrl,
        },
    };
}

export function remmoveBlockByBlockId(blocks: EditorJS.OutputBlockData[], blockId: string): EditorJS.OutputBlockData[] {
    return blocks.filter(block => block.id !== blockId);
}

export function getPostShareUrl(tokenName: string, postId: string): string {
    const baseUrl = `${getBaseUrl()}/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}`;
    if (getUserId()) return `${baseUrl}?refBy=${getUserId()}`;
    return baseUrl;
}

export function isPostPending(postId: string) {
    const post = getPost(postId);
    if (!post) return false;

    return post.status === PostStatus.PENDING;
}

export function ensurePostUrl(postSlugOrId: string, includeFlairId = true) {
    const post = getPostBySlugUrlOrId(postSlugOrId);
    if (!post || !post.slugUrl) return postSlugOrId;
    let url = post.slugUrl;

    if (includeFlairId && post.flairIds) {
        const flairId = post.flairIds[0];
        if (flairId) url = `${flairId}/${post.slugUrl}`;
    }
    // Add the "/community" prefix if the post was not created by a whitelisted user
    if (post.kind !== 'story') url = `community/${url}`;
    return url;
}
